import { NetworkStatus, useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { List, Text } from '@telegram-apps/telegram-ui';
import { GetQuestsQuery, GetQuestsQueryVariables, QuestMetricType } from '../client/generated/graphql';
import { getQuestsQuery } from '../client/queries/quest';
import { ListCategory } from '../client/types';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import { ConditionPickerViewListFooter } from '../components/common/ConditionPickerViewListFooter';
import { ConditionQuestBlock } from '../components/common/ConditionQuestBlock';
import { CustomInput } from '../components/common/CustomInput';
import { DateTimeCalendar } from '../components/common/DateTimeCalendar';
import { useVoice } from '../contexts/VoiceContext';
import { toDueString } from '../helpers/datetime';
import { findQuestByName, getVoicePostponeDate, getVoicePostponeToTomorrowDate } from '../helpers/quest';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedCategories } from '../hooks/caching/category';
import { useCachedQuestsWithFallback } from '../hooks/caching/quest';

const QUESTS_PAGE_SIZE = 20;
const DATETIME_MIN = '1970-01-01T00:00:00.000Z';
const DATETIME_MAX = '9999-12-31T23:59:59.999Z';

export const PostponeEditPage: FC = () => {
  const [searchParams] = useSearchParams();
  const voiceActionId = searchParams.get('voiceActionId');
  const navigate = useNavigate();
  const { pendingActions, onUpdateVoiceAction } = useVoice();
  const categories = useCachedCategories();

  const { data, networkStatus, fetchMore } = useQuery<
    GetQuestsQuery,
    GetQuestsQueryVariables
  >(getQuestsQuery, {
    variables: {
      limit: QUESTS_PAGE_SIZE,
      dueDateFrom: DATETIME_MIN,
      dueDateTo: DATETIME_MAX,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  });

  const allQuests = data?.quests ?? [];
  const hasMore = allQuests.length === QUESTS_PAGE_SIZE;
  const lastFetchedQuestId = allQuests.length > 0 ? allQuests[allQuests.length - 1].id : null;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const action = useMemo(
    () => (voiceActionId ? pendingActions.find((a) => a.id === voiceActionId) : null),
    [voiceActionId, pendingActions]
  );
  const postponeAction = action?.type === 'POSTPONE_QUEST' ? action : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedQuestId, setSelectedQuestId] = useState<string | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    []
  );

  const { quests: selectedQuests } = useCachedQuestsWithFallback(
    selectedQuestId ? [selectedQuestId] : null
  );

  const categoryMap = useMemo(
    () =>
      categories.reduce(
        (map: Record<string, ListCategory>, cat: ListCategory) => ({ ...map, [cat.id]: cat }),
        {} as Record<string, ListCategory>
      ),
    [categories]
  );

  const initialDate = useMemo(() => {
    if (!postponeAction) return dayjs().add(1, 'day').toDate();
    if (postponeAction.dueDateIso && dayjs(postponeAction.dueDateIso).isValid()) {
      return new Date(postponeAction.dueDateIso);
    }
    const quest =
      postponeAction.questId
        ? allQuests.find((q: { id: string }) => q.id === postponeAction.questId)
        : findQuestByName(allQuests, postponeAction.questName);
    if (quest) {
      return postponeAction.dueDateIso
        ? getVoicePostponeToTomorrowDate(quest).toDate()
        : getVoicePostponeDate(quest).toDate();
    }
    return dayjs().add(1, 'day').toDate();
  }, [postponeAction, allQuests]);

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  const postponeableQuests = useMemo(
    () =>
      [...selectedQuests, ...allQuests].filter(
        (q) => q.dueDate && !q.completedOn
      ),
    [selectedQuests, allQuests]
  );

  useEffect(() => {
    if (!postponeAction || allQuests.length === 0 || selectedQuestId) return;
    const found =
      postponeAction.questId
        ? postponeableQuests.find((q: { id: string }) => q.id === postponeAction.questId)
        : findQuestByName(postponeableQuests, postponeAction.questName);
    if (found) {
      setSelectedQuestId(found.id);
      if (!postponeAction.dueDateIso) {
        setSelectedDate(getVoicePostponeDate(found).toDate());
      }
    }
  }, [postponeAction, allQuests, postponeableQuests, selectedQuestId]);

  const filteredQuests = useMemo(() => {
    const unique = Array.from(new Map(postponeableQuests.map((q) => [q.id, q])).values());
    const query = searchQuery.toLowerCase();
    return query
      ? [...unique].filter((q) => q.name.toLowerCase().includes(query))
      : [...unique];
  }, [postponeableQuests, searchQuery]);

  const questNotFound =
    postponeAction &&
    postponeableQuests.length > 0 &&
    !selectedQuestId &&
    !(postponeAction.questId
      ? postponeableQuests.find((q: { id: string }) => q.id === postponeAction.questId)
      : postponeAction.questName && findQuestByName(postponeableQuests, postponeAction.questName));

  const isValid = !!selectedQuestId;

  const handleApply = useCallback(() => {
    if (!selectedQuestId || !voiceActionId) return;
    const q = postponeableQuests.find((pq) => pq.id === selectedQuestId);
    onUpdateVoiceAction(voiceActionId, {
      questId: selectedQuestId,
      questName: q?.name ?? '',
      displayTitle: q?.name ?? '',
      dueDateIso: selectedDate.toISOString(),
    });
    navigate(-1);
  }, [selectedQuestId, selectedDate, voiceActionId, onUpdateVoiceAction, postponeableQuests]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, []);

  const fetchMoreQuests = useCallback(() => {
    if (isFetchingMore || !hasMore || !lastFetchedQuestId) return;
    fetchMore({
      variables: {
        limit: QUESTS_PAGE_SIZE,
        startAtId: lastFetchedQuestId,
        dueDateFrom: DATETIME_MIN,
        dueDateTo: DATETIME_MAX,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.quests?.length) return prev;
        const existingIds = new Set(prev.quests.map((q) => q.id));
        const newQuests = fetchMoreResult.quests.filter((q) => !existingIds.has(q.id));
        return { ...prev, quests: [...prev.quests, ...newQuests] };
      },
    });
  }, [fetchMore, isFetchingMore, hasMore, lastFetchedQuestId]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 50) fetchMoreQuests();
    },
    [fetchMoreQuests]
  );

  const handleQuestSelect = useCallback((questId: string) => {
    setSelectedQuestId((prev) => (prev === questId ? null : questId));
  }, []);

  if (!postponeAction) {
    navigate(-1);
    return null;
  }

  return (
    <Page
      back
      insetEnabled={false}
      backClick={handleCancel}
      footer={
        <ButtonDock
          secondary={{ text: 'Cancel', onClick: handleCancel }}
          main={{
            text: 'Update action',
            onClick: handleApply,
            enabled: isValid,
            bgColor: getButtonThemeColors(true).backgroundColor,
          }}
        />
      }
    >
      <Stack flex={1} flexDirection="column" sx={{ minHeight: 0, overflow: 'hidden' }}>
        <Stack flexShrink={0} paddingLeft="10px" paddingRight="10px" paddingTop="5px" gap={0}>
          <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
            <Text>Postpone Quest</Text>
          </Stack>

          {!searchFocused && (
            <>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <CommonIcon icon="event" type="filled" size={16} color="var(--tg-theme-hint-color)" />
                <Text type="footnote" style={{ color: 'var(--tg-theme-hint-color)' }}>
                  {toDueString(dayjs(selectedDate))}
                </Text>
              </Stack>

              <DateTimeCalendar style={{ marginBottom: 3 }} value={selectedDate} onChange={setSelectedDate} />
            </>
          )}

          <CustomInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search quests..."
            wrapperStyle={{ minHeight: 0 }}
            onFocus={() => {
              if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
              }
              setSearchFocused(true);
            }}
            onBlur={() => {
              blurTimeoutRef.current = setTimeout(() => {
                blurTimeoutRef.current = null;
                setSearchFocused(false);
              }, 150);
            }}
          />

          {questNotFound && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'color-mix(in srgb, var(--tg-theme-destructive-text-color) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--tg-theme-destructive-text-color) 25%, transparent)',
              }}
            >
              <CommonIcon icon="warning" type="filled" size={20} color="var(--tg-theme-destructive-text-color)" />
              <Text style={{ fontSize: 14, color: 'var(--tg-theme-destructive-text-color)' }}>
                {postponeAction.questName ? `Quest "${postponeAction.questName}" not found. ` : 'Quest not found. '}Select a quest below.
              </Text>
            </Stack>
          )}
        </Stack>

        <List
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingLeft: 10, paddingRight: 10, paddingBottom: miniButtonDockScrollBottomPad }}
          onScroll={handleScroll}
        >
          {filteredQuests.map((quest) => {
            const category = categoryMap[quest.categoryId];
            if (!category) return null;
            return (
              <ConditionQuestBlock
                key={quest.id}
                quest={quest}
                category={category}
                metricType={QuestMetricType.Repeating}
                isSelected={selectedQuestId === quest.id}
                onToggle={() => handleQuestSelect(quest.id)}
              />
            );
          })}
          <ConditionPickerViewListFooter
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            filteredQuestsLength={filteredQuests.length}
          />
        </List>
      </Stack>
    </Page>
  );
};
